"use client";
import { useEffect, useState } from "react";
import Link from "next/link";
import { FileText, Paperclip, ArrowRight } from "lucide-react";
import { Button, Field, Input } from ".";
import { ReadState, useRead, type PageResult } from "./record-tools";
import { effectiveRoles, useSession } from "../../lib/session";
import { type AttachmentSummary, formatTime } from "../../lib/api";
import { AttachmentQuarantine } from "./attachment-quarantine";
import { CLUE_LABEL, type ClueDetail, type ClueSummary } from "./participation-model";
export type WorkOrg = { id: string; name: string; lifecycle?: string; children: WorkOrg[] };
export function useParticipationOrganizations() {
  const tree = useRead<{ items: WorkOrg[] }>("/api/v1/orgs/tree"),
    { whoami } = useSession();
  const flatten = (nodes: WorkOrg[]): WorkOrg[] =>
    nodes.flatMap((n) => [n, ...flatten(n.children ?? [])]);
  const nodes = flatten(tree.data?.items ?? []),
    allowed = new Set<string>();
  for (const grant of effectiveRoles(whoami).filter((g) => g.roleCode === "PARTICIPATION_REVIEWER"))
    for (const id of grant.scopeOrganizationIds) {
      const root = nodes.find((n) => n.id === id);
      if (!root) continue;
      for (const n of grant.scopeMode === "SUBTREE_DYNAMIC" ? flatten([root]) : [root])
        allowed.add(n.id);
    }
  return {
    ...tree,
    options: nodes
      .filter((n) => allowed.has(n.id) && n.lifecycle !== "ARCHIVED")
      .map((n) => ({ value: n.id, label: n.name }))
  };
}
export function ClueAttachments({ id }: { id: string }) {
  const read = useRead<{ items: AttachmentSummary[] }>(
    `/api/v1/attachments?${new URLSearchParams({ ownerType: "CLUE_SUBMISSION", ownerId: id })}`
  );
  return (
    <section className="participation-materials">
      <h3>
        <Paperclip size={16} />
        会员提交的附件
      </h3>
      <ReadState {...read} retry={read.reload} />
      {read.data?.items.length === 0 && <p className="record-muted">本条建言没有附件。</p>}
      <ul>
        {read.data?.items.map((file) => (
          <li key={file.id}>
            <FileText size={18} />
            <div>
              <strong>{file.fileName}</strong>
              <small>
                {Math.ceil(file.byteSize / 1024)} KB ·{" "}
                {file.status === "ACTIVE"
                  ? "可读取"
                  : file.status === "QUARANTINED"
                    ? "已隔离，停止分发"
                    : "已收档"}
              </small>
            </div>
            <AttachmentQuarantine file={file} ownerType="CLUE_SUBMISSION" onSaved={() => void read.reload()} />
            {file.status === "ACTIVE" && (
              <a
                className="record-link"
                href={`/api/v1/attachments/${encodeURIComponent(file.id)}/content`}
                download
              >
                下载
              </a>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
export function CluePicker({
  value,
  onChange,
  acceptedOnly = false,
  exclude = [],
  onReady
}: {
  value: ClueSummary | null;
  onChange: (item: ClueSummary | null) => void;
  acceptedOnly?: boolean;
  exclude?: string[];
  onReady?: (ready: boolean) => void;
}) {
  const [word, setWord] = useState(""),
    [q, setQ] = useState(""),
    [cursor, setCursor] = useState("");
  const read = useRead<PageResult<ClueSummary>>(
    `/api/v1/clue-intake?${new URLSearchParams({ q, limit: "10", ...(acceptedOnly ? { status: "ACCEPTED_FOR_FOLLOW_UP" } : {}), ...(cursor ? { cursor } : {}) })}`
  );
  const detail = useRead<ClueDetail>(
    value ? `/api/v1/clue-intake/${encodeURIComponent(value.id)}` : null
  );
  useEffect(() => {
    onReady?.(
      !!detail.data &&
        detail.data.status !== "MERGED" &&
        (!acceptedOnly || detail.data.status === "ACCEPTED_FOR_FOLLOW_UP")
    );
    return () => onReady?.(false);
  }, [detail.data, onReady, acceptedOnly]);
  return (
    <section className="participation-picker">
      <Field label="查找来源建言">
        <div className="record-search">
          <Input
            aria-label="建言标题或编号"
            value={word}
            onChange={(e) => setWord(e.target.value)}
            placeholder="输入标题或收件编号"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                setQ(word.trim());
                setCursor("");
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setQ(word.trim());
              setCursor("");
            }}
          >
            查找
          </Button>
        </div>
      </Field>
      <ReadState {...read} retry={read.reload} />
      <ul className="participation-pick-list">
        {read.data?.items
          .filter((c) => !exclude.includes(c.id) && c.status !== "MERGED")
          .map((c) => (
            <li key={c.id}>
              <button type="button" aria-pressed={value?.id === c.id} onClick={() => onChange(c)}>
                <strong>{c.title}</strong>
                <small>
                  {c.publicRef} · {CLUE_LABEL[c.status]} · {c.organization.name}
                </small>
              </button>
            </li>
          ))}
      </ul>
      {read.data?.items.filter((c) => !exclude.includes(c.id) && c.status !== "MERGED").length ===
        0 && <p className="record-muted">本批没有可选建言，可调整关键词或继续翻页。</p>}
      <div className="workspace-actions">
        {cursor && (
          <Button type="button" variant="outline" onClick={() => setCursor("")}>
            返回首批
          </Button>
        )}
        {read.data?.meta?.nextCursor && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setCursor(read.data!.meta!.nextCursor!)}
          >
            下一批
          </Button>
        )}
      </div>
      {value && (
        <div className="participation-selection">
          <div className="record-section-title">
            <strong>已选：{value.publicRef}</strong>
            <Button type="button" variant="ghost" onClick={() => onChange(null)}>
              取消选择
            </Button>
          </div>
          <ReadState {...detail} retry={detail.reload} />
          {detail.data && (
            <>
              <h3>{detail.data.title}</h3>
              <p>{detail.data.content.situation}</p>
              {detail.data.content.suggestion ? <p>{detail.data.content.suggestion}</p> : null}
            </>
          )}
        </div>
      )}
    </section>
  );
}
export function ParticipationTimeline({
  events,
  label
}: {
  events: Array<{
    occurredAt: string;
    toStatus: string;
    note?: string;
    memberMessage?: string;
    contentVersion?: number;
  }>;
  label: Record<string, string>;
}) {
  return (
    <div className="participation-timeline">
      {events.map((e, i) => (
        <article key={`${i}:${e.occurredAt}`}>
          <time>{formatTime(e.occurredAt)}</time>
          <h3>
            {label[e.toStatus] ?? e.toStatus}
            {e.contentVersion ? ` · 正文 v${e.contentVersion}` : ""}
          </h3>
          {(e.memberMessage || e.note) && <p>{e.memberMessage || e.note}</p>}
        </article>
      ))}
    </div>
  );
}
export function ParticipationLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link className="record-link" href={href}>
      {children}
      <ArrowRight size={14} />
    </Link>
  );
}
